/**
 * LaunchPage — Hub central de Polynator
 *
 * Ruta: /launch
 *
 * Hub para acceder a todos los módulos internos y herramientas externas.
 * Accesible desde el login normal (Redux) o desde el SSO Launcher (localStorage).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import { useRoleForTools } from '@domains/user';
import {
  setDefaultState,
  getGivenName,
} from '@domains/user/store/slices/user-slice/user-slice';
import { EXTERNAL_TOOLS, getCurrentEnvironment } from '../../config/external-tools';
import { openExternalTool } from '../../utils/external-tool.service';
import { clearLaunchAuth } from './launch.utils';

import {
  MAX_COLLAPSED,
  ITEM_HEIGHT,
  PANEL_HEADER_HEIGHT,
  PANEL_FOOTER_HEIGHT,
} from './launch.constants';
import { useAuthGuard }      from './hooks/useAuthGuard';
import { useVisibleModules } from './hooks/useVisibleModules';
import { LaunchNavBar }      from './components/LaunchNavBar';
import { ModulesPanel }      from './components/ModulesPanel';
import { ExternalToolsPanel } from './components/ExternalToolsPanel';
import {
  PageWrapper, PageFooter, WelcomeSection, WelcomeTitle, WelcomeSubtitle,
  HoverPreviewWrapper, HoverPreviewContent, HoverPreviewPlaceholder,
  HoverPreviewName, HoverPreviewSummary, HoverPreviewChips, HoverPreviewChip,
} from './styles';
import { HoverInfo } from './components/ModuleRow';

export const LaunchPage = () => {
  const navigate              = useNavigate();
  const dispatch              = useDispatch();
  const { ready, email }      = useAuthGuard();
  const visibleModules        = useVisibleModules();
  const env                   = getCurrentEnvironment();
  const givenName             = useSelector(getGivenName);
  const displayName           = givenName || email?.split('@')[0] || '';

  const { isLoading: isLoadingRoles } = useRoleForTools();
  const [hoveredItem, setHoveredItem] = useState<HoverInfo | null>(null);

  const hasModuleFooter = visibleModules.length > MAX_COLLAPSED;
  const maxVisibleItems = Math.max(
    Math.min(visibleModules.length, MAX_COLLAPSED),
    EXTERNAL_TOOLS.length,
  );
  const sharedPanelHeight =
    PANEL_HEADER_HEIGHT +
    maxVisibleItems * ITEM_HEIGHT +
    (hasModuleFooter ? PANEL_FOOTER_HEIGHT : 0);

  const handleOpenInternal = (path: string) => navigate(path);

  const handleLogout = () => {
    clearLaunchAuth();
    dispatch(setDefaultState());
    navigate('/launch-login', { replace: true });
  };

  if (!ready) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <PageWrapper>
      <LaunchNavBar
        visibleModules={visibleModules}
        onOpenModule={handleOpenInternal}
        onOpenTool={openExternalTool}
        onLogout={handleLogout}
      />

      <Container style={{
        paddingTop: '4.5rem',
        paddingBottom: env === 'local' ? '4rem' : '2rem',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}>

        {!!email && (
          <WelcomeSection>
            <WelcomeTitle>
              Hola, <span>{displayName}</span>
            </WelcomeTitle>
            <WelcomeSubtitle>Selecciona un módulo o herramienta para comenzar</WelcomeSubtitle>
          </WelcomeSection>
        )}

        <Row className="g-4" style={{ alignItems: 'flex-start' }}>

          {!!email && (
            <Col xs={12} md={6}>
              <ModulesPanel
                modules={visibleModules}
                height={sharedPanelHeight}
                isLoading={isLoadingRoles}
                onOpenModule={handleOpenInternal}
                onHover={setHoveredItem}
              />
            </Col>
          )}

          <Col xs={12} md={!!email ? 6 : 12}>
            <ExternalToolsPanel height={sharedPanelHeight} />
          </Col>

        </Row>

        <HoverPreviewWrapper $active={!!hoveredItem}>
          <HoverPreviewPlaceholder $visible={false} />

          <HoverPreviewContent $visible={!!hoveredItem}>
            <HoverPreviewName>{hoveredItem?.name ?? ''}</HoverPreviewName>
            {hoveredItem?.summary && (
              <HoverPreviewSummary>{hoveredItem.summary}</HoverPreviewSummary>
            )}
            <HoverPreviewChips>
              {(hoveredItem?.bullets ?? []).map((chip, i) => (
                <HoverPreviewChip key={chip} $color={hoveredItem?.chipColors?.[i]}>
                  {chip}
                </HoverPreviewChip>
              ))}
            </HoverPreviewChips>
          </HoverPreviewContent>
        </HoverPreviewWrapper>
      </Container>

      {env === 'local' && (
        <PageFooter>
          <strong>Modo Localhost</strong> — Asegúrate de que las herramientas estén corriendo en sus puertos locales.
        </PageFooter>
      )}
    </PageWrapper>
  );
};
